import { EventSourceEnum } from '../Opportunity/types'

export const enum MedSatisfactionLevel {
  Calm = 5,
  Manageable = 4,
  Rough = 3,
  Overwhelming = 2,
  Drowning = 1
}

export enum MedsModalType {
  None = 0,
  Primary = 1,
  Secondary = 2
}

export enum MedicationDateType {
  LastMonth = 1,
  LastYear = 2,
  OverAYearAgo = 3,
  SpecificDate = 4,
  IDontRemember = 5
}

export interface IMedicationDate {
  option?: MedicationDateType
  date?: string
}

export interface IMedStrength {
  gpi?: string
  value?: string | number
}

export interface IMedFormStrengths {
  form?: string
  strengths?: IMedStrength[]
}

export interface ISearchedMed {
  drug_name: string
  dosage_forms: IMedFormStrengths[]
}

export interface IAddMedication {
  gpi: string
  start_date_of_service?: string
  start_date_type?: MedicationDateType | null
  drug_name: string
  drug_dosage_form: string
  drug_strength: string
  start_reason: string
  end_date_of_service: string
  end_reason: string
}

export interface IMedication {
  _created: string
  auto_id: number
  // read by the medication screens, absent from the store's own reads
  current?: boolean
  start_date_type?: string
  maintenance_drug_indicator?: string
  tier_level_code?: string
  prescribing_physician_phone?: string
  // from the linked claim; null for manually added medications
  pharmacy_name?: string | null
  pharmacy_telephone_number?: string | null
  change_owner_id: number
  coinsurance_amount: string
  date_of_service: string
  days_supply: string
  drug_dosage_form: string
  drug_name: string
  drug_strength: string
  drug_type: string
  end_date_of_service: string | null
  generated_id: string
  gpi: string
  insertion_type: string
  jet_generated_id: string
  ndc: string
  net_amount_due: string
  patient_pay_amount: string
  prescribing_physician_first_name: string
  prescribing_physician_last_name: string
  prescribing_physician_npi: string
  quantity_dispensed: string
  start_date_of_service: string
  total_paid_amount: string
  transaction_id: string
  user_id: number
}

export interface IMedicationHistoryItem {
  _created: string
  auto_id: number
  change_owner_id: number
  coinsurance_amount: string
  days_supply: string
  drug_dosage_form: string
  drug_name: string
  drug_strength: string
  drug_type: string
  end_date_of_service: string | null
  generated_id: string
  gpi: string
  insertion_type?: string
  jet_generated_id: string
  ndc: string
  net_amount_due: string
  patient_pay_amount: string
  prescribing_physician_first_name: string
  prescribing_physician_last_name: string
  prescribing_physician_npi: string
  quantity_dispensed: string
  rx_gpi: string
  start_date_of_service: string
  total_paid_amount: string
  transaction_id: string
  user_id: number
}

export interface IMedicationsPage {
  page: number
  has_next_page: boolean
  results: IMedication[]
}

export interface GroupedMedsHistory {
  [year: string]: {
    [month: string]: {
      [day: string]: IMedicationHistoryItem[]
    }
  }
}

export interface PrescriberInfo {
  name: string
  npi: string
}

export interface PharmacyInfo {
  name: string
  phone_number: string
  address: string
  city?: string | null
  prescribers: PrescriberInfo[]
}

export interface PharmaciesInfo {
  pharmacies: PharmacyInfo[]
}

export interface ICLinicalImpactOption {
  id: number
  name: string
  _created: string
}

export interface ICondition {
  id: number
  name: string
  visible: boolean
  _created: string
}

export interface IClinicalImpactLogOption {
  id: number
  option_id: number
  _created: string
}

export interface IClinicalImpactLog {
  id: number
  user_id: number
  associated_medication: string
  conditions: string
  options: IClinicalImpactLogOption[]
  _created: string
  medication_details?: string[]
}

export enum MedicalImpactType {
  Interchange = 1,
  Deprescribe = 2,
  ChangeDaysSupply = 3,
  TransferPharmacy = 4,
  CopayCard = 5,
  BrandToGeneric = 6,
  InterchangeWithinSameDrugClass = 7,
  InterchangeToDifferentDrugClass = 8,
  MarkCubansCostPlusPharmacy = 9,
  HighRiskDrugInteraction = 10,
  AvoidedDrug = 11
}

export enum MedicalImpactStatus {
  Active = 1,
  Pending = 2,
  Confirmed = 3
}

export enum ProviderResponse {
  Accepted = 1,
  Rejected = 2,
  Pending = 3
}

export interface IMedicalImpactLog {
  id: number
  medications: string
  type_change: MedicalImpactType
  status: MedicalImpactStatus
  _created: string
  gpis?: string[]
  conversation_date?: string | null
  medication_details?: string[]
}

export interface IClinicalImpactCosts {
  client_before_cost?: number
  client_before_days?: number
  client_after_cost?: number
  client_after_days?: number
  member_before_cost?: number
  member_before_days?: number
  member_after_cost?: number
  member_after_days?: number
}

export interface MedsState {
  // Fields
  error: string
  lastPrimaryBannerShown: Date | null
  lastSecondaryBannerShown: Date | null
  lastUpdated: string
  medsHomeBannerCooldown: boolean
  onboardingMedSatisfaction: MedSatisfactionLevel | null

  // Actions
  getPharmacyInfo(userId: number): Promise<PharmaciesInfo | null>
  getCurrentMedications(
    userId: number
  ): Promise<{ lastUpdated: string; results: IMedication[] } | null>
  getStoppedMedications(userId: number): Promise<IMedication[] | null>
  searchMedsByName(name: string): Promise<ISearchedMed[]>
  searchUserConditionsByName(
    userId: number,
    name: string
  ): Promise<ICondition[]>
  searchUserClinicalOptionsByName(
    userId: number,
    name: string
  ): Promise<ICLinicalImpactOption[]>
  stopMedication(
    userId: number,
    medId: number | string | undefined,
    medDate: string | null,
    endReason: string
  ): Promise<boolean>
  addMedication(
    userId: number,
    med: IAddMedication
  ): Promise<{ success: boolean; errorMessage: string }>
  markAsCurrentMedication(userId: number, auto_id: number): Promise<boolean>
  getMemberMedicationsHistory(
    userId: number,
    name: string,
    gpi: string
  ): Promise<GroupedMedsHistory | null>
  getMedicationsPage(
    userId: number,
    page: number
  ): Promise<IMedicationsPage | null>
  setPrimaryBannerCooldown(): void
  setSecondaryBannerCooldown(): void
  shouldDisplayDialog(): MedsModalType
  setMedsHomeBannerCooldown(cooldown: boolean): void
  getClinicalImpactOptions(
    userId: number
  ): Promise<ICLinicalImpactOption[] | null>
  getMemberClinicalImpactLogs(
    userId: number
  ): Promise<IClinicalImpactLog[] | null>
  addClinicalImpactLog(
    userId: number,
    conditions: number[],
    options: number[],
    medication?: string,
    gpis?: string[],
    status?: MedicalImpactStatus,
    costs?: IClinicalImpactCosts,
    provider_outreach?: ProviderResponse,
    event_source?: EventSourceEnum,
    user_opportunity_id?: number
  ): Promise<{ success: boolean; errorMessage: string }>
  getMemberMedicalImpactLogs(
    userId: number
  ): Promise<IMedicalImpactLog[] | null>
  addMedicalImpactLog(
    userId: number,
    typeOfChange: MedicalImpactType,
    status: MedicalImpactStatus,
    medication?: string,
    gpis?: string[],
    conversationDate?: string
  ): Promise<{ success: boolean; errorMessage: string }>
  clear(): void
}
